/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * Azure Entra ID Redirect Handler
 *
 * This component handles the OAuth redirect from Azure and cleans up the URL
 * to prevent infinite loops when using the browser back button.
 */

import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { ROUTE_PATH } from "@/constants/routeConstants";
import { useVerifiedSession } from "@/hooks/useVerifiedSession";

export const AuthRedirectHandler = () => {
  const { instance, inProgress } = useMsal();
  const isMsalAuthenticated = useIsAuthenticated();
  const { isAuthenticated: isReduxAuthenticated, user } = useSelector(
    (state: RootState) => state.auth
  );
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessedRedirect = useRef(false);
  const processedUrls = useRef(new Set<string>());
  const isHandlingPopState = useRef(false);
  
  // Only consider user authenticated if MSAL and Redux agree on the same identity
  // This prevents redirect loops after logout or a Microsoft account switch
  const isVerified = useVerifiedSession();
  const isAuthenticated = isMsalAuthenticated && isReduxAuthenticated && !!user && isVerified;

  // Handle OAuth redirect
  useEffect(() => { // NOSONAR
    const handleRedirect = async () => {
      // Don't process if MSAL is already handling something
      if (inProgress !== "none") {
        return;
      }

      // Check if URL contains OAuth parameters
      const urlParams = new URLSearchParams(location.search);
      const hasAuthParams =
        urlParams.has("code") ||
        urlParams.has("state") ||
        urlParams.has("error");
      const currentUrl = location.pathname + location.search;

      // Don't process the same URL twice
      if (processedUrls.current.has(currentUrl)) {
        return;
      }

      if (hasAuthParams && !hasProcessedRedirect.current) {
        processedUrls.current.add(currentUrl);
        hasProcessedRedirect.current = true;

        try {
          const response = await instance.handleRedirectPromise();

          if (response && response.account) {
            const isOnLoginPath = location.pathname === ROUTE_PATH.ROUTE_LOGIN;
            const targetPath = isOnLoginPath
              ? ROUTE_PATH.ROUTE_DASHBOARD
              : location.pathname;

            // Navigate with replace to avoid adding to history
            navigate(targetPath, { replace: true });
          } else if (urlParams.has("error")) {
            // Handle error case (user cancelled, error occurred, etc.)
            if (import.meta.env.DEV) {
              console.warn(
                "OAuth error:",
                urlParams.get("error"),
                urlParams.get("error_description")
              );
            }
            navigate(ROUTE_PATH.ROUTE_LOGIN, { replace: true });
          } else {
            navigate(ROUTE_PATH.ROUTE_LOGIN, { replace: true });
          }
        } catch (error) {
          console.error("Error handling redirect:", error);
          navigate(ROUTE_PATH.ROUTE_LOGIN, { replace: true });
        }
      }
    };

    handleRedirect();
  }, [
    instance,
    navigate,
    location.search,
    location.pathname,
    inProgress,
  ]);

  // Prevent authenticated users from navigating back to login page
  useEffect(() => {
    const handlePopState = () => {
      // Prevent re-entrant calls
      if (isHandlingPopState.current) {
        return;
      }

      // If user is authenticated and on login page, redirect to dashboard
      if (isAuthenticated && location.pathname === ROUTE_PATH.ROUTE_LOGIN) {
        isHandlingPopState.current = true;
        navigate(ROUTE_PATH.ROUTE_DASHBOARD, { replace: true });

        // Reset flag after navigation
        setTimeout(() => {
          isHandlingPopState.current = false;
        }, 100);
      }
    };

    // Add listener for browser back/forward button
    window.addEventListener("popstate", handlePopState);

    // Also check on mount and when authentication/location changes
    if (isAuthenticated && location.pathname === ROUTE_PATH.ROUTE_LOGIN) {
      navigate(ROUTE_PATH.ROUTE_DASHBOARD, { replace: true });
    }

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isAuthenticated, location.pathname, navigate, isMsalAuthenticated, isReduxAuthenticated, user]);

  return null;
};
